import type { MeditationStep } from './meditations';

/**
 * The manifestation toolkit: three practices from the prototype's toolkit tab,
 * each walked through a step at a time on the same stepper the guided
 * meditations use.
 *
 * **The prototype's copy is deliberately not carried over.** It said EFT
 * "clears money blocks, fear, and old stories from the nervous system" and
 * that blessed water "reprograms the body toward abundance". Those are
 * biological claims, which the safety directive forbids outright, and they are
 * not claims this product can stand behind. Each practice is described here as
 * what the member actually does and what tradition it comes from; what it is
 * FOR is attention and intention, which is a claim about the practice rather
 * than about the body.
 */

export const COACHING_MANIFESTATION_KEYS = ['eft', 'water-blessing', 'quantum-jump'] as const;
export type CoachingManifestationKey = (typeof COACHING_MANIFESTATION_KEYS)[number];

export const COACHING_MANIFESTATION_DISCLAIMER =
  'These are reflective practices drawn from popular traditions. They are not medical or psychological treatment, they make no change to your body, and they are not a substitute for professional help.';

export type BuiltInManifestationPractice = {
  key: CoachingManifestationKey;
  title: string;
  /** The tradition it comes from, said plainly. */
  origin: string;
  description: string;
  steps: MeditationStep[];
};

export const BUILT_IN_MANIFESTATION_PRACTICES: readonly BuiltInManifestationPractice[] = [
  {
    key: 'eft',
    title: 'Tapping',
    origin:
      'From EFT, a self-help technique in which a sequence of points is tapped while the thing being worked on is said out loud.',
    description:
      'You name the thing you are avoiding, say it plainly several times, and tap through a short sequence while you do. What it asks of you is to keep saying the true version rather than the tidy one.',
    steps: [
      {
        text: 'Name one thing about money, or about the goal, that you would rather not say out loud. Say it to yourself in the plainest words you have.',
        seconds: 45,
      },
      {
        text: 'Rate how heavy it feels right now, one to ten. Do not talk yourself out of the number.',
        seconds: 30,
      },
      {
        text: 'Tap the side of your hand and say: even though this is true, I am going to be alright. Repeat it three times.',
        seconds: 60,
      },
      {
        text: 'Tap gently through the points as you keep naming it: the top of your head, the inner eyebrow, the side of the eye, under the eye.',
        seconds: 75,
      },
      {
        text: 'Continue: under the nose, the chin, the collarbone, under the arm. Keep saying the plain version.',
        seconds: 75,
      },
      {
        text: 'Stop and take one slow breath. Rate it again, one to ten. Whatever the number is, that is the number.',
        seconds: 45,
      },
    ],
  },
  {
    key: 'water-blessing',
    title: 'Water blessing',
    origin:
      'From a popular tradition of speaking an intention over water before drinking it. The ritual is the point; nothing is claimed about the water itself.',
    description:
      'A one-minute practice to bookend a day with. It is a way of saying a thing you mean, out loud, to something you are about to do anyway.',
    steps: [
      { text: 'Pour a glass of water and hold it in both hands.', seconds: 25 },
      {
        text: 'Say your intention over it, out loud if you can. Present tense, short, and something you actually believe is possible.',
        seconds: 45,
      },
      { text: 'Say thank you for one thing you already have. Be specific.', seconds: 35 },
      { text: 'Drink it slowly, without doing anything else at the same time.', seconds: 45 },
    ],
  },
  {
    key: 'quantum-jump',
    title: 'Quantum jumping',
    origin:
      'A visualisation from the popular manifestation tradition: imagining a version of yourself who already lives the thing you are working toward.',
    description:
      'Five minutes spent as the person who already has it, paying attention to the ordinary details rather than the triumphant ones. What you bring back is a way of carrying yourself, not a prediction.',
    steps: [
      { text: 'Close your eyes and take three slow breaths.', seconds: 30 },
      {
        text: 'Picture a version of you, a few years on, who already has the thing you are working toward. Do not look at the achievement. Look at their Tuesday morning.',
        seconds: 75,
      },
      {
        text: 'What are they doing with their hands? What is the room like? What is unremarkable to them that would not be to you?',
        seconds: 80,
      },
      {
        text: 'Step into them. Sit the way they sit. Notice how the thing you are worried about registers from in there, if it registers at all.',
        seconds: 80,
      },
      {
        text: 'Ask them for one thing you could do this week. Take the first answer, not the impressive one.',
        seconds: 60,
      },
      {
        text: 'Come back to the room and hold on to that one thing. It is the only part of this you are meant to act on.',
        seconds: 45,
      },
    ],
  },
];

export function isManifestationKey(key: string | null): key is CoachingManifestationKey {
  return key !== null && (COACHING_MANIFESTATION_KEYS as readonly string[]).includes(key);
}
